import styled from "styled-components"

import { useAppSelector } from "store/hooks"
import { userLogoSelector } from "store/tempDataSlise"
import { ReactComponent as IconArrowDownTray } from "assets/svg/export-icon.svg"
import { ReactComponent as IconDocumentDuplicate } from "assets/svg/icon-document-duplicate.svg"
import defaultLogo from "assets/svg/logo-blue.svg"

const ToolbarBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 32px;
  gap: 16px;
  min-height: 48px;

  @media (max-width: 568px) {
    padding: 8px 24px;
    gap: 12px;
    flex-direction: column;
    align-items: flex-start;
  }

  @media print {
    display: none;
  }
`

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`

const LogoWrap = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    height: 32px;
    width: auto;
    max-width: 160px;
    object-fit: contain;
  }
`

const Divider = styled.span`
  width: 1px;
  height: 16px;
  background: #e2e8f0;
  flex-shrink: 0;
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const Title = styled.h1`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.2;
`

const Subtitle = styled.span`
  font-size: 12px;
  color: #64748b;
  line-height: 1.2;
`

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`

const Pill = styled.button<{ $variant: "primary" | "secondary" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 32px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  ${({ $variant }) =>
    $variant === "primary"
      ? `
        background: #42e8ff;
        color: #134f66;
        border: 1px solid #42e8ff;
      `
      : `
        background: #ffffff;
        color: #2db2c8;
        border: 1px solid #42e8ff;
      `}

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  svg path {
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

interface Props {
  title: string
  subtitle?: string
  showActions: boolean
  onDownloadPdf: () => void
  onCopyLink: () => void
  isDownloading: boolean
  isDownloadDisabled: boolean
}

export function Toolbar({
  title,
  subtitle,
  showActions,
  onDownloadPdf,
  onCopyLink,
  isDownloading,
  isDownloadDisabled,
}: Props) {
  const userLogo = useAppSelector(userLogoSelector)
  const logoSrc = userLogo ? `${userLogo}?cors=logo` : defaultLogo

  return (
    <ToolbarBar>
      <Left>
        <LogoWrap>
          <img src={logoSrc} alt="logo" />
        </LogoWrap>
        <Divider />
        <TitleBlock>
          <Title>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </TitleBlock>
      </Left>
      {showActions && (
        <Right>
          <Pill type="button" $variant="secondary" onClick={onCopyLink}>
            <IconDocumentDuplicate />
            Copy link
          </Pill>
          <Pill type="button" $variant="primary" onClick={onDownloadPdf} disabled={isDownloadDisabled}>
            <IconArrowDownTray />
            {isDownloading ? "Generating..." : "Download PDF"}
          </Pill>
        </Right>
      )}
    </ToolbarBar>
  )
}
